import { tokenizePhraseWords } from '@src/editor/services/karaokeWordTiming';
import {
  endsSentenceIgnoringAbbreviations,
  needsSpaceBetweenPhrases,
  visualTextWidth,
} from '@src/utility/strings';

import type { TranscriptElement } from '@src/types.ts/transcript';
import type { TranslationData } from '@src/types.ts/translation';

/**
 * SRT subtitle export. Builds a downloadable `.srt` from the
 * transcript currently in view — the original words, or a translation's
 * phrases when a translated view is active.
 *
 * There is no SRT *writer* elsewhere in the app (SRT is otherwise
 * import-only), and `formatTimecode` in `transcriptFormatUtils` is
 * `HH:MM:SS` only. This module adds the millisecond `-->` timecode and a
 * standards-based cue chunker.
 *
 * Cue segmentation follows the widely-used Netflix/BBC subtitle
 * conventions rather than one-cue-per-sentence or per-paragraph: cues are
 * bounded by line length, on-screen duration, and reading speed, and break
 * at sentence boundaries where possible. The tunables below are the knobs.
 */

/** Max characters on a single subtitle line. */
const MAX_CHARS_PER_LINE = 42;
/** Max lines shown per cue. */
const MAX_LINES_PER_CUE = 2;
/** Max on-screen duration for a single cue, seconds. */
const MAX_CUE_DURATION_S = 5;
/** Min on-screen duration for a single cue, seconds (~5/6s). */
const MIN_CUE_DURATION_S = 5 / 6;
/** Max reading speed; a cue must stay up long enough to read at this rate. */
const MAX_CHARS_PER_SECOND = 17;

/** A word (or translated token) with a start/end time in seconds. */
/** The minimum a word must expose for line joining. */
export interface JoinableWord {
  text: string;
  /**
   * Whether whitespace preceded this word in its source text. Absent means
   * yes — the space-delimited default every existing producer relies on.
   */
  joinWithSpace?: boolean;
}

export interface TimedWord extends JoinableWord {
  text: string;
  startTs: number;
  endTs: number;
}

interface SrtCue {
  startTs: number;
  endTs: number;
  lines: string[];
}

/** Format seconds as an SRT timecode: `HH:MM:SS,mmm`. */
export function formatSrtTimecode(seconds: number): string {
  const safe = Number.isFinite(seconds) && seconds > 0 ? seconds : 0;
  const totalMs = Math.round(safe * 1000);
  const ms = totalMs % 1000;
  const totalSeconds = Math.floor(totalMs / 1000);
  const s = totalSeconds % 60;
  const m = Math.floor(totalSeconds / 60) % 60;
  const h = Math.floor(totalSeconds / 3600);
  const pad = (n: number, width = 2) => n.toString().padStart(width, '0');
  return `${pad(h)}:${pad(m)}:${pad(s)},${pad(ms, 3)}`;
}

/** True when `text` ends a clause (comma, semicolon, colon, dash + closers). */
function endsClause(text: string): boolean {
  return /[,;:—–]["'”’)\]]*$/.test(text.trim());
}

/**
 * Re-join words the way the SOURCE text had them (PLAT-11505).
 *
 * A predicate cannot decide this: "both sides full-width" gets `ほぼ30年`
 * wrong the moment ASCII appears inside Japanese (`ほぼ 30 年`), and the
 * ASCII is a product name or a number often enough to matter. So each word
 * carries whether whitespace preceded it in the text it came from, and the
 * join simply replays that — the same rule the translated captions view uses
 * for its word atoms.
 */
function joinWords(words: readonly JoinableWord[]): string {
  let out = '';
  for (const w of words) {
    if (!out) out = w.text;
    else out += spaceBefore(w) ? ` ${w.text}` : w.text;
  }
  return out;
}

/** Whitespace preceded this word in its source text. Default yes (Latin). */
const spaceBefore = (word: JoinableWord) => word.joinWithSpace !== false;

/**
 * Greedily wrap words into lines no wider than `MAX_CHARS_PER_LINE`.
 *
 * Width is VISUAL (PLAT-11468): a full-width character occupies two Latin
 * columns, so counting code units let a Japanese line run twice as wide as
 * the limit intends.
 */
function wrapWords(words: readonly JoinableWord[]): string[] {
  const lines: string[] = [];
  let line = '';
  for (const word of words) {
    if (!line) {
      line = word.text;
      continue;
    }
    const candidate = spaceBefore(word)
      ? `${line} ${word.text}`
      : `${line}${word.text}`;
    if (visualTextWidth(candidate) <= MAX_CHARS_PER_LINE) {
      line = candidate;
    } else {
      lines.push(line);
      line = word.text;
    }
  }
  if (line) lines.push(line);
  return lines;
}

function makeCue(words: TimedWord[]): SrtCue {
  return {
    startTs: words[0].startTs,
    endTs: words[words.length - 1].endTs,
    lines: wrapWords(words),
  };
}

/**
 * Group timed words into cues honoring the line/duration limits. Breaks at
 * sentence boundaries always, and at clause boundaries (commas etc.) once
 * the cue already holds at least a full line — spoken transcripts are often
 * sparsely punctuated, so clause breaks keep cues from cutting mid-phrase.
 * A long sentence is split when it would overflow; a short one becomes its
 * own (short) cue.
 */
function chunkIntoCues(words: TimedWord[]): SrtCue[] {
  const cues: SrtCue[] = [];
  let current: TimedWord[] = [];

  const flush = () => {
    if (current.length) {
      cues.push(makeCue(current));
      current = [];
    }
  };

  for (const word of words) {
    if (current.length) {
      const tentative = [...current, word];
      const wouldOverflowLines =
        wrapWords(tentative).length > MAX_LINES_PER_CUE;
      const wouldOverrun = word.endTs - current[0].startTs > MAX_CUE_DURATION_S;
      if (wouldOverflowLines || wouldOverrun) flush();
    }
    current.push(word);
    if (endsSentenceIgnoringAbbreviations(word.text)) {
      flush();
    } else if (
      endsClause(word.text) &&
      visualTextWidth(joinWords(current)) >= MAX_CHARS_PER_LINE
    ) {
      flush();
    }
  }
  flush();

  // Reading-speed / min-duration pass: extend cues that are too brief to
  // read, but never past the next cue's start (no overlap).
  for (let i = 0; i < cues.length; i += 1) {
    const cue = cues[i];
    const chars = cue.lines.reduce(
      (sum, l, i) => sum + visualTextWidth(l) + (i > 0 ? 1 : 0),
      0,
    );
    const needed = Math.max(MIN_CUE_DURATION_S, chars / MAX_CHARS_PER_SECOND);
    if (cue.endTs - cue.startTs < needed) {
      const nextStart =
        i + 1 < cues.length ? cues[i + 1].startTs : Number.POSITIVE_INFINITY;
      cue.endTs = Math.max(
        cue.endTs,
        Math.min(cue.startTs + needed, nextStart),
      );
    }
  }

  return cues;
}

/** Serialize cues into SRT text. */
export function buildSrt(words: TimedWord[]): string {
  const cues = chunkIntoCues(words);
  if (cues.length === 0) return '';
  return `${cues
    .map(
      (cue, i) =>
        `${i + 1}\n${formatSrtTimecode(cue.startTs)} --> ${formatSrtTimecode(
          cue.endTs,
        )}\n${cue.lines.join('\n')}\n`,
    )
    .join('\n')}`;
}

/**
 * Flatten original transcript elements into timed words: each text element
 * with timestamps is one word, trailing punctuation is attached to it,
 * leading punctuation (an opening quote/bracket, before any word) is prepended
 * to the following word, and whitespace marks a word boundary. Removed/cut
 * content is skipped.
 */
export function elementsToTimedWords(
  elements: TranscriptElement[],
): TimedWord[] {
  const words: TimedWord[] = [];
  let current: TimedWord | null = null;
  // Leading punctuation (e.g. an opening `"`/`(`) seen before a word; prepended
  // to the next text element instead of being dropped or misattached.
  let prefix = '';

  for (const el of elements) {
    if (el.state === 'removed' || el.state === 'cut') continue;
    if (el.type === 'text') {
      // Kept text elements carry timestamps (only removed ones are null,
      // and those are skipped above). Each starts a new word.
      if (current) words.push(current);
      current = {
        text: prefix + (el.value ?? ''),
        startTs: el.ts,
        endTs: el.end_ts,
      };
      prefix = '';
    } else {
      // Non-text element: whitespace marks a word boundary; other punctuation
      // attaches to the preceding word, or — when there is none yet — buffers
      // as a prefix for the following word.
      const value = el.value ?? '';
      if (value.trim() === '') {
        if (current) {
          words.push(current);
          current = null;
        }
      } else if (current) {
        current.text += value;
      } else {
        prefix += value;
      }
    }
  }
  if (current) words.push(current);

  return words.filter(w => w.text.trim().length > 0);
}

/**
 * Flatten a translation into timed words. Translations are stored at phrase
 * granularity with no timestamps of their own, so each phrase's span is
 * derived by joining its `originalIndices` back to the source elements'
 * `ts`/`end_ts` (only non-removed text elements present in `elements`
 * count — this scopes the export to what is in view). The phrase's
 * `translatedValue` is then split into words with time distributed evenly
 * across them so the chunker can segment it like any other word stream.
 */
export function translationToTimedWords(
  elements: TranscriptElement[],
  translation: TranslationData,
): TimedWord[] {
  const elementByIndex = new Map<number, TranscriptElement>();
  for (const el of elements) {
    if (el.state === 'removed' || el.state === 'cut') continue;
    elementByIndex.set(el.initial_index, el);
  }

  const phrases = [...translation.phrases]
    // Only active fragments count for render/search (retired ones are dropped
    // from the views), so exports must match — exclude `active: false`.
    .filter(p => p.active !== false && p.originalIndices.length > 0)
    .sort(
      (a, b) => Math.min(...a.originalIndices) - Math.min(...b.originalIndices),
    );

  const words: TimedWord[] = [];
  for (const phrase of phrases) {
    let startTs: number | null = null;
    let endTs: number | null = null;
    for (const idx of phrase.originalIndices) {
      const el = elementByIndex.get(idx);
      if (el?.type !== 'text') continue;
      if (typeof el.ts === 'number')
        startTs = startTs === null ? el.ts : Math.min(startTs, el.ts);
      if (typeof el.end_ts === 'number')
        endTs = endTs === null ? el.end_ts : Math.max(endTs, el.end_ts);
    }
    if (startTs === null || endTs === null || endTs <= startTs) continue;
    const spanStart = startTs;

    const value = phrase.translatedValue?.trim();
    if (!value) continue;

    // PLAT-11505 — a spaceless phrase split on whitespace is ONE token: too
    // wide to wrap and impossible to break a cue at, which is what let
    // Japanese cues run past their maximum duration. The segmenter finds real
    // word boundaries; Latin/Cyrillic tokenize exactly as before. The
    // separators are kept (as `joinWithSpace`) rather than discarded, so
    // re-joining reproduces the phrase verbatim — including ASCII embedded in
    // Japanese, which no both-sides-wide predicate can get right.
    const tokens: JoinableWord[] = [];
    let sawWhitespace = false;
    for (const piece of tokenizePhraseWords(value).pieces) {
      if (piece.trim() === '') {
        sawWhitespace = true;
        continue;
      }
      // Only stamp the flag when it is FALSE, so a space-delimited language
      // produces byte-identical words to before this ticket. The phrase's
      // FIRST token has no whitespace of its own to replay — the boundary
      // with the previous phrase is decided like the inline views decide it
      // (PLAT-11636: always spacing it put a gap between Japanese phrases).
      const previous = words[words.length - 1];
      const spaced =
        tokens.length === 0
          ? !previous || needsSpaceBetweenPhrases(previous.text, piece)
          : sawWhitespace;
      tokens.push(
        spaced ? { text: piece } : { text: piece, joinWithSpace: false },
      );
      sawWhitespace = false;
    }
    if (tokens.length === 0) continue;
    const per = (endTs - startTs) / tokens.length;
    tokens.forEach((token, i) =>
      words.push({
        ...token,
        startTs: spanStart + i * per,
        endTs: spanStart + (i + 1) * per,
      }),
    );
  }

  return words;
}
